import * as React from 'react';
import { FieldError } from 'react-hook-form';

import Chatbot from '../../../types/Chatbot';
import SettingsService from '../../../services/SettingsService/SettingsService';
import CustomInput from '../../../components/CustomInput/CustomInput';
import Button from '../../../components/Button/Button';
import LabelWithInfo from '../../../components/LabelWithInfo/LabelWithInfo';

interface RateLimitSectionProps {
  readonly chatbot: Chatbot;
  readonly canEdit: boolean;
  readonly refreshChatbots: () => Promise<void>;
  readonly onSuccess: (message: string) => void;
  readonly onError: (message: string) => void;
}

type RateLimitField = 'rateLimit' | 'rateLimitTotal';

function formatNumber(value: number): string {
  return value.toLocaleString('en-GB');
}

function rangeError(value: string): FieldError | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  if (!/^\d+$/.test(trimmed) || Number(trimmed) < 1) {
    return {
      type: 'validate',
      message: 'Enter a whole number of 1 or more, or leave blank.'
    };
  }
  return undefined;
}

function toInputValue(limit: number | null | undefined): string {
  return limit == null ? '' : String(limit);
}

function toLimit(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : Number(trimmed);
}

function RateLimitSection({
  chatbot,
  canEdit,
  refreshChatbots,
  onSuccess,
  onError
}: RateLimitSectionProps): React.ReactElement {
  const [perVisitor, setPerVisitor] = React.useState(
    toInputValue(chatbot.rateLimit)
  );
  const [total, setTotal] = React.useState(
    toInputValue(chatbot.rateLimitTotal)
  );
  const [serverErrors, setServerErrors] = React.useState<
    Partial<Record<RateLimitField, string>>
  >({});
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setPerVisitor(toInputValue(chatbot.rateLimit));
    setTotal(toInputValue(chatbot.rateLimitTotal));
    setServerErrors({});
  }, [chatbot.id, chatbot.rateLimit, chatbot.rateLimitTotal]);

  const perVisitorError =
    rangeError(perVisitor) ??
    (serverErrors.rateLimit
      ? { type: 'server', message: serverErrors.rateLimit }
      : undefined);
  const totalError =
    rangeError(total) ??
    (serverErrors.rateLimitTotal
      ? { type: 'server', message: serverErrors.rateLimitTotal }
      : undefined);
  const isUnchanged =
    toLimit(perVisitor) === (chatbot.rateLimit ?? null) &&
    toLimit(total) === (chatbot.rateLimitTotal ?? null);
  const canSave = !isSaving && !isUnchanged && !perVisitorError && !totalError;

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;

    try {
      setIsSaving(true);
      await SettingsService.updateRateLimits(
        chatbot.id,
        toLimit(perVisitor),
        toLimit(total)
      );
      await refreshChatbots();
      onSuccess('Rate limits updated');
    } catch (err: any) {
      const fieldErrors = err.response?.data?.fieldErrors;
      if (fieldErrors) {
        setServerErrors(fieldErrors);
      } else {
        onError(err.message || 'Something went wrong');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='w-full max-w-md border-t mt-8 pt-6'>
      <h3 className='text-sm font-semibold'>Rate limits</h3>

      <div className='mt-4 space-y-2'>
        <LabelWithInfo
          htmlFor='rate-limit-per-visitor'
          info={
            'The most messages one visitor can send to this bot in a minute. ' +
            `Leave blank to use the default of ${formatNumber(chatbot.defaultRateLimit)}.`
          }
        >
          Messages per visitor per minute
        </LabelWithInfo>
        <CustomInput
          id='rate-limit-per-visitor'
          value={perVisitor}
          onChange={(e) => {
            setPerVisitor(e.target.value);
            setServerErrors({ ...serverErrors, rateLimit: undefined });
          }}
          placeholder={String(chatbot.defaultRateLimit)}
          disabled={!canEdit || isSaving}
          error={perVisitorError}
        />
      </div>

      <div className='mt-4 space-y-2'>
        <LabelWithInfo
          htmlFor='rate-limit-total'
          info={
            'The most messages this bot will answer in a minute across all visitors. ' +
            'Leave blank for no limit.'
          }
        >
          Total messages per minute
        </LabelWithInfo>
        <CustomInput
          id='rate-limit-total'
          value={total}
          onChange={(e) => {
            setTotal(e.target.value);
            setServerErrors({ ...serverErrors, rateLimitTotal: undefined });
          }}
          disabled={!canEdit || isSaving}
          error={totalError}
        />
      </div>

      {canEdit && (
        <div className='flex justify-end mt-4'>
          <Button
            type='button'
            loading={isSaving}
            disabled={!canSave}
            onClick={handleSave}
          >
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
}

export default RateLimitSection;
